import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '@/projects/shuttle/store/store';
import { axiosInstance } from '@/utils/AxiosInstance';
import { AxiosError } from 'axios';
import { showToasterWithDelay } from '@/store/slices/toasterSlice';

// Types definition
export interface DepositRequest {
  tour_search_request_id: number;
  tour_id: number;
  pickup_bus_stop_id: number;
  dropoff_bus_stop_id: number;
  from_time: string;
  to_time: string;
  kind: 'paid' | 'unpaid';
}

interface ValidationError {
  code: number;
  errors: Array<{ field: string; message: string }>;
  message: string;
  status_code: number;
}

export interface DepositResponse {
  redirect_url: string | null;
}

interface DepositState {
  isLoading: boolean;
  redirectUrl: string | null;
  error: string | null;
}

// Initial state
const initialState: DepositState = {
  isLoading: false,
  redirectUrl: null,
  error: null
};

// Create deposit async thunk
export const createDepositAsync = createAsyncThunk<
  DepositResponse,
  { depositData: DepositRequest; phone: string },
  { dispatch: AppDispatch; state: RootState }
>(
  'deposit/createDepositAsync',
  async ({ depositData, phone }, { dispatch, rejectWithValue }) => {
    try {
      // Set phone authorization and make the request
      const response = await axiosInstance
        .setPhoneHeader(phone)
        .post<DepositResponse>('/deposits', depositData);
      
      return response;
    } catch (err) {
      const error = err as AxiosError;
      let message = 'Произошла ошибка при создании депозита.';
      
      if (error.message === 'Network Error' || error.message.includes('CORS')) {
        message = 'Ошибка сети. Проверьте подключение и попробуйте снова.';
      } else {
        const data = error.response?.data as ValidationError;
        if (data && data.errors) {
          const errors = data.errors.map((e) => e.message);
          message += '\n' + errors.join('\r\n');
        }
      }
      
      dispatch(
        showToasterWithDelay({
          type: 'warning',
          message: message,
          isVisible: true,
        })
      );
      
      return rejectWithValue(message);
    }
  }
);

// Create slice
const depositSlice = createSlice({
  name: 'depositReducer',
  initialState,
  reducers: {
    resetDeposit: (state) => {
      state.redirectUrl = null;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createDepositAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createDepositAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.redirectUrl = action.payload.redirect_url;
      })
      .addCase(createDepositAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || action.error.message || 'Неизвестная ошибка';
      });
  }
});

export const { resetDeposit } = depositSlice.actions;

export const depositSelector = (state: RootState) => state.depositReducer;

export default depositSlice.reducer; 